import * as cornerstone from 'cornerstone-core';
import * as cornerstoneTools from 'cornerstone-tools';

/**
 * Utility for drawing annotations from findings data onto Cornerstone viewports
 */

/**
 * Draw freehand annotations from findings data
 * @param {HTMLElement} element - The Cornerstone-enabled element
 * @param {Array} freehandData - Array of freehand annotation objects
 */
export const drawFreehandAnnotations = (element, freehandData) => {
  if (!element || !freehandData || freehandData.length === 0) {
    return;
  }

  console.log('drawFreehandAnnotations called with', freehandData.length, 'annotations');

  try {
    // Process each freehand annotation and add it individually
    freehandData.forEach((annotation, idx) => {
      console.log(`Processing freehand annotation ${idx}:`, annotation);

      if (!annotation.handles || annotation.handles.length === 0) {
        console.warn(`Annotation ${idx} has no handles`);
        return;
      }

      // Convert handles to points array
      const points = [];
      annotation.handles.forEach(handle => {
        points.push({
          x: handle.x,
          y: handle.y,
          highlight: false,
          active: false
        });
      });

      console.log(`Created ${points.length} points for annotation`);

      // Set color based on shape or margin type
      let color = 'yellow'; // Default color
      if (annotation.shape && annotation.shape.color_rgb) {
        const rgb = annotation.shape.color_rgb;
        color = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
      } else if (annotation.margin && annotation.margin.color_rgb) {
        const rgb = annotation.margin.color_rgb;
        color = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
      }
      console.log(`Annotation color: ${color}`);

      // Create the annotation data structure that cornerstoneTools FreehandRoi expects
      const annotationData = {
        visible: annotation.visible !== undefined ? annotation.visible : true,
        active: false,
        invalidated: true,
        uuid: `freehand-${Date.now()}-${idx}`,
        handles: {
          points: points,
          textBox: {
            active: false,
            hasMoved: false,
            movesIndependently: false,
            drawnIndependently: true,
            allowedOutsideImage: true,
            hasBoundingBox: true
          }
        },
        meanStdDev: {},
        area: 0,
        color: color
      };

      console.log('Adding annotation data:', annotationData);

      // Add this annotation to the tool state
      cornerstoneTools.addToolState(element, 'FreehandRoi', annotationData);
      console.log(`Added annotation ${idx} to FreehandRoi tool state`);
    });

    // Get the final tool state to verify
    const finalToolState = cornerstoneTools.getToolState(element, 'FreehandRoi');
    console.log('Final FreehandRoi toolState:', finalToolState);
    console.log('Calling cornerstone.updateImage()');

    // Update the image to show the annotations
    cornerstone.updateImage(element);

    console.log('cornerstone.updateImage() completed');
  } catch (error) {
    console.error('Error drawing freehand annotations:', error);
  }
};

/**
 * Draw marks (point annotations) from findings data
 * @param {HTMLElement} element - The Cornerstone-enabled element
 * @param {Array} marksData - Array of mark objects
 */
export const drawMarks = (element, marksData) => {
  if (!element || !marksData || marksData.length === 0) {
    return;
  }

  console.log('drawMarks called with', marksData.length, 'marks');

  try {
    // Process each mark and add it individually
    marksData.forEach((mark, idx) => {
      console.log(`Processing probe mark ${idx}:`, mark);

      if (!mark.handles || !mark.handles.end) {
        console.warn(`Mark ${idx} has no handles.end`);
        return;
      }

      // Set color
      let color = undefined;
      if (mark.color_rgb && Array.isArray(mark.color_rgb) && mark.color_rgb.length > 0) {
        const rgb = mark.color_rgb[0];
        color = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
      }

      // Create the probe data structure
      const probeData = {
        visible: mark.visible !== undefined ? mark.visible : true,
        active: false,
        invalidated: true,
        uuid: `probe-${Date.now()}-${idx}`,
        handles: {
          end: {
            x: mark.handles.end.x,
            y: mark.handles.end.y,
            highlight: false,
            active: false
          }
        },
        color: color
      };

      console.log(`Adding probe mark at (${probeData.handles.end.x}, ${probeData.handles.end.y}) with color ${color}`);

      // Add this mark to the tool state
      cornerstoneTools.addToolState(element, 'Probe', probeData);
      console.log(`Added mark ${idx} to Probe tool state`);
    });

    // Get the final tool state to verify
    const finalToolState = cornerstoneTools.getToolState(element, 'Probe');
    console.log('Final Probe toolState:', finalToolState);
    console.log('Calling cornerstone.updateImage() for marks');

    // Update the image to show the marks
    cornerstone.updateImage(element);

    console.log('Marks update completed');
  } catch (error) {
    console.error('Error drawing marks:', error);
  }
};

/**
 * Clear all annotations from an element
 * @param {HTMLElement} element - The Cornerstone-enabled element
 */
export const clearAnnotations = (element) => {
  if (!element) {
    return;
  }

  try {
    console.log('Clearing annotations...');

    // Clear all tool state for FreehandRoi
    cornerstoneTools.clearToolState(element, 'FreehandRoi');
    console.log('Cleared FreehandRoi annotations');

    // Clear all tool state for Probe  
    cornerstoneTools.clearToolState(element, 'Probe');
    console.log('Cleared Probe annotations');

    // Update the image to reflect the cleared state
    cornerstone.updateImage(element);
    console.log('Annotations cleared and image updated');
  } catch (error) {
    console.error('Error clearing annotations:', error);
  }
};

/**
 * Find the target image index where annotations should be drawn
 * @param {Object} findingsData - The findings data object with rawData.stacks
 * @param {string|number} stackId - The stack ID to search for
 * @returns {number|null} The first image index with findings, or null if not found
 */
export const findTargetImageIndex = (findingsData, stackId) => {
  if (!findingsData?.rawData?.stacks) {
    return null;
  }

  const matchingStack = findingsData.rawData.stacks.find(
    stack => String(stack.stackId) === String(stackId)
  );

  if (!matchingStack?.currentImageIdIndex || matchingStack.currentImageIdIndex.length === 0) {
    return null;
  }

  // Return the first image index with findings
  return matchingStack.currentImageIdIndex[0];
};

/**
 * Draw all annotations from a findings object onto the appropriate viewport
 * @param {Array} viewportRefs - Array of viewport element references
 * @param {Object} findingsData - The findings data object with rawData.stacks
 * @param {Object} currentImageInfo - Current image information (stackId, imageIndex)
 */
export const drawFindingsForCurrentImage = (viewportRefs, findingsData, currentImageInfo) => {
  if (!viewportRefs || viewportRefs.length === 0 || !findingsData || !currentImageInfo) {
    console.warn('Missing required data for drawing findings');
    return;
  }

  const { stackId, imageIndex } = currentImageInfo;

  console.log('=== Drawing Findings Debug ===');
  console.log('Drawing findings for stackId:', stackId, 'imageIndex:', imageIndex);
  console.log('findingsData:', findingsData);

  // Get the stacks from rawData
  if (!findingsData.rawData || !findingsData.rawData.stacks) {
    console.warn('No rawData.stacks in findings data');
    return;
  }

  const stacks = findingsData.rawData.stacks;
  console.log('Total stacks available:', stacks.length);
  console.log('Stack IDs:', stacks.map(s => s.stackId));

  // Find the stack that matches the current stackId
  const matchingStack = stacks.find(stack => String(stack.stackId) === String(stackId));

  if (!matchingStack) {
    console.warn(`No matching stack found for stackId: ${stackId}`);
    console.log('Available stackIds:', stacks.map(s => s.stackId));
    return;
  }

  console.log('Matching stack found:', matchingStack);
  console.log('Stack currentImageIdIndex:', matchingStack.currentImageIdIndex);

  // Check if this stack has findings for the current image index
  if (!matchingStack.currentImageIdIndex || !matchingStack.currentImageIdIndex.includes(imageIndex)) {
    console.log('No findings for current image index. Looking for:', imageIndex, 'Available:', matchingStack.currentImageIdIndex);
    return;
  }

  // Get the active viewport (assuming first viewport for now)
  const element = viewportRefs[0];
  if (!element) {
    console.warn('No viewport element available');
    return;
  }

  console.log('Viewport element found:', element);

  // Clear existing annotations first
  clearAnnotations(element);

  // Draw freehand annotations if present
  if (matchingStack.freehand && matchingStack.freehand.length > 0) {
    console.log(`Drawing ${matchingStack.freehand.length} freehand annotation(s)`);
    console.log('Freehand data:', matchingStack.freehand);
    drawFreehandAnnotations(element, matchingStack.freehand);
  }

  // Draw probe marks if present
  if (matchingStack.probe && matchingStack.probe.length > 0) {
    console.log(`Drawing ${matchingStack.probe.length} probe mark(s)`);
    console.log('Probe data:', matchingStack.probe);
    drawMarks(element, matchingStack.probe);
  }

  // Draw other mark types if present (marks property for other annotations)
  if (matchingStack.marks && matchingStack.marks.length > 0) {
    console.log(`Drawing ${matchingStack.marks.length} mark(s)`);
    drawMarks(element, matchingStack.marks);
  }

  console.log('Successfully drew findings on the image');
  console.log('=== End Drawing Findings Debug ===');
};

const annotationDrawer = {
  drawFreehandAnnotations,
  drawMarks,
  clearAnnotations,
  drawFindingsForCurrentImage,
  findTargetImageIndex
};

export default annotationDrawer;
